import { useEffect, useRef, useCallback } from 'react';
import { useParticipant, ClickEvent, ScrollEvent } from '../../contexts/ParticipantContext';
import DynamicUIRenderer from '../DynamicUIRenderer';

interface InteractiveUIViewerProps {
    uiSpec: Record<string, unknown>;
    onInteraction?: (eventType: string, data: unknown) => void;
}

export default function InteractiveUIViewer({ uiSpec, onInteraction }: InteractiveUIViewerProps) {
    const { dispatch } = useParticipant();
    const containerRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<number>(Date.now());
    const clickEventsRef = useRef<ClickEvent[]>([]);
    const scrollEventsRef = useRef<ScrollEvent[]>([]);
    const maxScrollDepthRef = useRef<number>(0);
    const lastScrollTimeRef = useRef<number>(0);

    // Track time on page
    useEffect(() => {
        startTimeRef.current = Date.now();
        clickEventsRef.current = [];
        scrollEventsRef.current = [];
        maxScrollDepthRef.current = 0;

        const interval = setInterval(() => {
            dispatch({
                type: 'UPDATE_INTERACTION',
                payload: {
                    time_on_page_ms: Date.now() - startTimeRef.current,
                    click_events: clickEventsRef.current,
                    scroll_events: scrollEventsRef.current,
                    max_scroll_depth: maxScrollDepthRef.current
                }
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [dispatch]);

    // Track clicks
    const handleClick = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;

        const clickEvent: ClickEvent = {
            timestamp: Date.now(),
            element_id: target.id || target.className?.toString().slice(0, 50) || 'unknown',
            element_type: target.tagName.toLowerCase(),
            element_text: target.textContent?.slice(0, 50),
            x: event.clientX,
            y: event.clientY
        };

        clickEventsRef.current.push(clickEvent);
        onInteraction?.('click', clickEvent);
    }, [onInteraction]);

    // Track scroll with throttling
    const handleScroll = useCallback(() => {
        if (!containerRef.current) return;

        // Throttle to 1 event per second
        const now = Date.now();
        if (now - lastScrollTimeRef.current < 1000) return;
        lastScrollTimeRef.current = now;

        const container = containerRef.current;
        const scrollHeight = container.scrollHeight - container.clientHeight;
        const scrollDepth = scrollHeight > 0
            ? Math.round((container.scrollTop / scrollHeight) * 100)
            : 0;

        if (scrollDepth > maxScrollDepthRef.current) {
            maxScrollDepthRef.current = scrollDepth;
        }

        const scrollEvent: ScrollEvent = {
            timestamp: now,
            scroll_depth_percent: scrollDepth,
            scroll_y: container.scrollTop
        };

        scrollEventsRef.current.push(scrollEvent);
        onInteraction?.('scroll', scrollEvent);
    }, [onInteraction]);

    // Attach event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('click', handleClick);
        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('click', handleClick);
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleClick, handleScroll]);

    return (
        <div
            ref={containerRef}
            className="relative border-2 border-gray-200 rounded-lg bg-white overflow-auto"
            style={{ minHeight: '400px', maxHeight: '70vh' }}
        >
            {/* Indicator that this is the interactive area */}
            <div className="sticky top-0 left-0 right-0 bg-blue-50 border-b border-blue-200 px-3 py-2 text-sm text-blue-700 flex items-center gap-2 z-10">
                <span>👆</span>
                <span>Explore a interface abaixo - clique, role e interaja normalmente</span>
            </div>

            {/* Rendered UI */}
            <div className="p-4">
                <DynamicUIRenderer uiSpec={uiSpec} />
            </div>
        </div>
    );
}
